import { useState, useEffect, useCallback, Fragment, useMemo } from 'react';
import { supabase, callEdgeFunction } from './supabaseClient';
import {
  fetchSimpraCheck,
  buildDiscountGroupLookupIndex,
  resolveEffectiveAppliedDiscountId,
} from './simpraCheck';
import { SimpraCheckDetailPanel } from './SimpraCheckDetailPanel';
import './QRCodesPage.css';

const STATUS_LABELS = {
  active: 'Aktif',
  used: 'Kullanıldı',
  expired: 'Süresi doldu',
  pending: 'Beklemede',
  cancelled: 'İptal',
};

function StatusBadge({ status }) {
  const key = status && STATUS_LABELS[status] ? status : 'default';
  return (
    <span className={`qr-badge qr-badge--${key}`}>
      {STATUS_LABELS[status] || status || '—'}
    </span>
  );
}

function fmt(val) {
  if (val === null || val === undefined) return '—';
  return val;
}

function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return val;
  }
}

function fmtAmount(val) {
  if (val === null || val === undefined) return '—';
  return Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortId(id) {
  if (!id) return '—';
  return `${id.slice(0, 8)}…`;
}

/** Fallback when shops.simpra_property_id is not set yet. */
const KNOWN_BRANCH_BY_PROPERTY = {
  'eb404fd6-e8ab-4773-8a85-19201501b5cb': 'Bahçeşehir',
  '83487dea-9b7c-4071-8abd-1443eed16cf7': 'Arenapark',
  '70bdb2bd-c2c0-4b53-9e44-8844b7ea35a7': 'Merkez',
  'c1d2375e-1f8d-43f8-86b2-f9acb589a44f': 'Yeşilköy',
  'b04bc6d1-9860-4a3b-824b-5aa67091e795': 'Taksim',
  'ce6d5ee2-7e55-42ea-b003-d4ca543f2b6a': 'Nişantaşı',
};

function shopShort(name) {
  return String(name || '')
    .replace(/^MAKE\s*ME\s*JOI\s*/i, '')
    .trim();
}

function branchNameForProperty(propertyId, shopByProperty) {
  if (!propertyId) return null;
  const key = String(propertyId).toLowerCase();
  const fromShop = shopShort(shopByProperty?.get(key)?.shop);
  if (fromShop) return fromShop;
  return KNOWN_BRANCH_BY_PROPERTY[key] || null;
}

/** Gifted stamps: used QR with zero amounts and no Simpra check. */
function isGiftedStampQr(r) {
  if (!r || r.status !== 'used' || r.check_id) return false;
  // Explicit gift marker (amounts set to 0 by admin_gift_stamps).
  if (
    r.total_amount != null
    && r.discount_amount != null
    && r.original_amount != null
    && r.original_tax_amount != null
    && Number(r.total_amount) === 0
    && Number(r.discount_amount) === 0
    && Number(r.original_amount) === 0
    && Number(r.original_tax_amount) === 0
  ) {
    return true;
  }
  return false;
}

function isNotPaidQr(r) {
  if (!r || r.status !== 'used' || isGiftedStampQr(r)) return false;
  return (
    r.total_amount == null
    && r.discount_amount == null
    && r.original_amount == null
    && r.original_tax_amount == null
  );
}

const PAGE_SIZE = 20;

const QR_CODE_SORT_COLS = new Set([
  'id',
  'status',
  'option',
  'quantity',
  'total_amount',
  'discount_amount',
  'original_amount',
  'original_tax_amount',
  'created_at',
  'used_at',
  'last_event_at',
  'expires_at',
  'property_id',
  'check_id',
  'creator_id',
]);

function MusteriCell({ creatorId, info }) {
  if (!creatorId) {
    return <span className="qr-customer-empty">—</span>;
  }
  if (!info) {
    return (
      <span title={creatorId} className="qr-mono">
        {shortId(creatorId)}
      </span>
    );
  }
  const hasAny = info.full_name || info.email || info.phone;
  return (
    <div className="qr-customer" title={creatorId}>
      {info.full_name ? <div className="qr-customer-name">{info.full_name}</div> : null}
      {info.email ? <div className="qr-customer-email">{info.email}</div> : null}
      {info.phone ? <div className="qr-customer-phone">{info.phone}</div> : null}
      {!hasAny ? <span className="qr-customer-empty">Profil alanı yok</span> : null}
    </div>
  );
}

function AssignedDiscountCell({ creatorId, info }) {
  const id = info?.discount_id;
  if (!creatorId) {
    return <span className="qr-customer-empty">—</span>;
  }
  if (!id) {
    return <span className="qr-customer-empty" title="Kullanıcı metadata">atanmadı</span>;
  }
  return (
    <span title={`Kullanıcı metadata: ${id}`} className="qr-mono qr-mono--dim">
      {shortId(id)}
    </span>
  );
}

function AppliedDiscountCell({ appliedDiscountId, storedAppliedId, looksGlobal }) {
  if (!appliedDiscountId) {
    return <span className="qr-customer-empty">henüz yok</span>;
  }
  return (
    <span
      title={
        looksGlobal
          ? `Kayıtlı global ID (yanlış): ${storedAppliedId}\nProperty indirim ID: ${appliedDiscountId}`
          : `qr_codes.applied_discount_id: ${appliedDiscountId}`
      }
      className={`qr-mono qr-applied-discount${looksGlobal ? ' qr-applied-discount--warn' : ''}`}
    >
      {shortId(appliedDiscountId)}
      <span className="qr-applied-badge">{looksGlobal ? 'düzelt' : 'uygulandı'}</span>
    </span>
  );
}

function CheckCell({ checkId, expanded }) {
  if (!checkId) {
    return <span className="qr-customer-empty">—</span>;
  }
  return (
    <span className={`qr-check-id${expanded ? ' qr-check-id--open' : ''}`}>
      <span className="qr-mono qr-mono--dim qr-check-id-full" title={checkId}>{checkId}</span>
      <span className="qr-check-chevron" aria-hidden>{expanded ? '▾' : '▸'}</span>
    </span>
  );
}

export default function QRCodesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userById, setUserById] = useState(() => new Map());
  const [userLookupError, setUserLookupError] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(null);
  const [discountTotal, setDiscountTotal] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = hide expired noise
  // Default: newest created QRs first (Siparişler uses used_at instead)
  const [sortCol, setSortCol] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [checkCache, setCheckCache] = useState(() => new Map());
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState('');
  const [expiredTotal, setExpiredTotal] = useState(null);
  const [expiredCountLoading, setExpiredCountLoading] = useState(false);
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const [discountGroupsList, setDiscountGroupsList] = useState([]);
  const [shops, setShops] = useState([]);
  const groupIndex = useMemo(
    () => buildDiscountGroupLookupIndex(discountGroupsList),
    [discountGroupsList],
  );

  const shopByProperty = useMemo(() => {
    const map = new Map();
    shops.forEach((s) => {
      const pid = String(s.simpra_property_id || '').toLowerCase();
      if (pid) map.set(pid, s);
    });
    return map;
  }, [shops]);

  const handleAppliedSaved = useCallback((qrRowId, propertyDiscountId) => {
    if (!qrRowId || !propertyDiscountId) return;
    setRows((prev) => prev.map((row) => (
      row.id === qrRowId
        ? { ...row, applied_discount_id: propertyDiscountId }
        : row
    )));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('simpra_discount_groups')
          .select(`
            global_discount_id,
            simpra_discount_group_members (
              property_id,
              property_discount_id,
              shop_label
            )
          `);
        if (cancelled) return;
        if (error) {
          setDiscountGroupsList([]);
          return;
        }
        setDiscountGroupsList(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setDiscountGroupsList([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('shops')
        .select('id, shop, simpra_property_id');
      if (!cancelled) setShops(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  const sortColDb = QR_CODE_SORT_COLS.has(sortCol) ? sortCol : 'created_at';
  const sortAscDb = QR_CODE_SORT_COLS.has(sortCol) ? sortAsc : false;

  const toggleCheckRow = (row) => {
    if (!row.check_id) return;
    setExpandedRowId((prev) => (prev === row.id ? null : row.id));
  };

  const loadSimpraCheck = useCallback(async (checkId, propertyId) => {
    setCheckCache((prev) => {
      const existing = prev.get(checkId);
      if (existing?.loading || existing?.data) return prev;
      const next = new Map(prev);
      next.set(checkId, { loading: true, error: null, data: null });
      return next;
    });
    try {
      const data = await fetchSimpraCheck(checkId, propertyId);
      setCheckCache((prev) => {
        const next = new Map(prev);
        next.set(checkId, { loading: false, error: null, data });
        return next;
      });
    } catch (e) {
      setCheckCache((prev) => {
        const next = new Map(prev);
        next.set(checkId, {
          loading: false,
          error: e?.message || 'Check yüklenemedi',
          data: null,
        });
        return next;
      });
    }
  }, []);

  const refreshExpiredCount = useCallback(async () => {
    setExpiredCountLoading(true);
    try {
      const { count, error: countErr } = await supabase
        .from('qr_codes')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'expired');
      if (countErr) throw countErr;
      setExpiredTotal(count ?? 0);
    } catch {
      setExpiredTotal(null);
    } finally {
      setExpiredCountLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      key: 'property_id',
      label: 'Şube',
      sortable: true,
      render: (r) => {
        const gifted = isGiftedStampQr(r);
        const notPaid = isNotPaidQr(r);
        const name = branchNameForProperty(r.property_id, shopByProperty);
        return (
          <span className="qr-id-line">
            {gifted ? <span className="qr-gift-badge">HEDİYE</span> : null}
            {notPaid ? <span className="qr-not-paid-badge">NOT PAID</span> : null}
            {name ? (
              <span className="qr-branch" title={r.property_id || undefined}>{name}</span>
            ) : (
              <span title={r.property_id || undefined} className="qr-mono qr-mono--dim">
                {shortId(r.property_id)}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Durum',
      sortable: true,
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: 'musteri',
      label: 'Müşteri',
      sortable: false,
      wrap: true,
      render: (r) => <MusteriCell creatorId={r.creator_id} info={userById.get(r.creator_id)} />,
    },
    {
      key: 'option',
      label: 'Seçenek',
      sortable: true,
      render: (r) => fmt(r.option),
    },
    {
      key: 'quantity',
      label: 'Adet',
      sortable: true,
      render: (r) => fmt(r.quantity),
    },
    {
      key: 'total_amount',
      label: 'Toplam',
      sortable: true,
      render: (r) => <span className="qr-amount">{fmtAmount(r.total_amount)}</span>,
    },
    {
      key: 'discount_amount',
      label: 'İndirim',
      sortable: true,
      render: (r) => <span className="qr-amount">{fmtAmount(r.discount_amount)}</span>,
    },
    {
      key: 'assigned_discount_id',
      label: 'Atanan ID',
      sortable: false,
      render: (r) => (
        <AssignedDiscountCell
          creatorId={r.creator_id}
          info={userById.get(r.creator_id)}
        />
      ),
    },
    {
      key: 'applied_discount_id',
      label: 'Property indirim ID',
      sortable: false,
      render: (r) => {
        const assigned = userById.get(r.creator_id)?.discount_id;
        const effective = resolveEffectiveAppliedDiscountId({
          storedAppliedId: r.applied_discount_id,
          assignedGlobalId: assigned,
          propertyId: r.property_id,
          groupIndex,
        });
        const looksGlobal = Boolean(
          r.applied_discount_id
          && groupIndex.isGlobalId(r.applied_discount_id),
        );
        return (
          <AppliedDiscountCell
            appliedDiscountId={effective}
            storedAppliedId={r.applied_discount_id}
            looksGlobal={looksGlobal}
          />
        );
      },
    },
    {
      key: 'original_amount',
      label: 'Orijinal',
      sortable: true,
      render: (r) => <span className="qr-amount">{fmtAmount(r.original_amount)}</span>,
    },
    {
      key: 'original_tax_amount',
      label: 'Vergi',
      sortable: true,
      render: (r) => <span className="qr-amount">{fmtAmount(r.original_tax_amount)}</span>,
    },
    {
      key: 'created_at',
      label: 'Oluşturulma',
      sortable: true,
      render: (r) => <span className="qr-date">{fmtDate(r.created_at)}</span>,
    },
    {
      key: 'used_at',
      label: 'Kullanım',
      sortable: true,
      render: (r) => <span className="qr-date">{fmtDate(r.used_at)}</span>,
    },
    {
      key: 'expires_at',
      label: 'Bitiş',
      sortable: true,
      render: (r) => <span className="qr-date">{fmtDate(r.expires_at)}</span>,
    },
    {
      key: 'check_id',
      label: 'Check',
      sortable: true,
      render: (r) => (
        <CheckCell
          checkId={r.check_id}
          expanded={expandedRowId === r.id}
        />
      ),
    },
  ], [userById, groupIndex, expandedRowId, shopByProperty]);

  const applyStatusFilter = useCallback((query) => {
    // Default: active + used + cancelled + pending (hide expired TTL noise).
    if (!statusFilter) {
      return query.in('status', ['active', 'used', 'cancelled', 'pending']);
    }
    if (statusFilter === 'all') return query;
    return query.eq('status', statusFilter);
  }, [statusFilter]);

  const fetchDiscountTotal = useCallback(async () => {
    const pageSize = 1000;
    let from = 0;
    let sum = 0;
    for (;;) {
      let sumQuery = supabase
        .from('qr_codes')
        .select('discount_amount')
        .range(from, from + pageSize - 1);
      sumQuery = applyStatusFilter(sumQuery);
      if (search.trim()) {
        sumQuery = sumQuery.ilike('option', `%${search.trim()}%`);
      }
      const { data, error: sumErr } = await sumQuery;
      if (sumErr) throw sumErr;
      const batch = data || [];
      for (const row of batch) {
        const n = Number(row.discount_amount);
        if (Number.isFinite(n)) sum += n;
      }
      if (batch.length < pageSize) break;
      from += pageSize;
    }
    return sum;
  }, [applyStatusFilter, search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUserLookupError(null);
    try {
      const orderCol = QR_CODE_SORT_COLS.has(sortColDb) ? sortColDb : 'created_at';
      let query = supabase
        .from('qr_codes')
        .select('*', { count: 'exact' })
        .order(orderCol, { ascending: sortAscDb, nullsFirst: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      query = applyStatusFilter(query);
      if (search.trim()) {
        query = query.ilike('option', `%${search.trim()}%`);
      }

      const [listResult, discountSum] = await Promise.all([
        query,
        fetchDiscountTotal().catch(() => null),
      ]);
      const { data, error: qErr, count } = listResult;
      if (qErr) throw qErr;
      const list = data || [];
      setDiscountTotal(discountSum);

      const creatorIds = [...new Set(list.map((r) => r.creator_id).filter(Boolean))];
      const map = new Map();
      if (creatorIds.length > 0) {
        try {
          const users = await callEdgeFunction('admin-get-users-public-info', {
            method: 'POST',
            body: { p_ids: creatorIds },
          });
          (users || []).forEach((u) => {
            map.set(u.user_id, u);
          });
        } catch (e) {
          setUserLookupError(e?.message || 'Kullanıcı bilgisi alınamadı.');
        }
      }
      setUserById(map);
      setRows(list);
      setTotal(count);
    } catch (e) {
      setError(e.message || 'Veri yüklenemedi.');
    } finally {
      setLoading(false);
      refreshExpiredCount();
    }
  }, [page, search, sortColDb, sortAscDb, applyStatusFilter, refreshExpiredCount, fetchDiscountTotal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (col) => {
    if (!QR_CODE_SORT_COLS.has(col)) return;
    if (sortCol === col) {
      setSortAsc((a) => !a);
    } else {
      setSortCol(col);
      setSortAsc(true);
    }
    setPage(0);
  };

  const countExpiredQrCodes = () => expiredTotal ?? 0;

  const showExpiredInList = () => {
    setStatusFilter('expired');
    setPage(0);
    setCleanupConfirmOpen(false);
  };

  const requestCleanup = () => {
    setCleanupMessage('');
    setError(null);
    if (expiredTotal === 0) {
      setCleanupMessage('Silinecek süresi dolmuş kayıt yok.');
      return;
    }
    setCleanupConfirmOpen(true);
  };

  const cancelCleanup = () => {
    setCleanupConfirmOpen(false);
  };

  const confirmCleanup = async () => {
    setCleanupLoading(true);
    setCleanupConfirmOpen(false);
    setCleanupMessage('');
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_cleanup_expired_qr_codes');
      if (rpcErr) throw rpcErr;
      const deleted = Number(data ?? 0);
      setCleanupMessage(
        deleted > 0
          ? `${deleted.toLocaleString('tr-TR')} süresi dolmuş kayıt silindi. Aktif ve kullanılmış kayıtlar korundu.`
          : 'Silinecek süresi dolmuş kayıt kalmamıştı.',
      );
      setExpandedRowId(null);
      if (page !== 0) setPage(0);
      else await fetchData();
    } catch (e) {
      setError(e.message || 'Temizleme başarısız.');
    } finally {
      setCleanupLoading(false);
    }
  };

  const totalPages = total !== null ? Math.ceil(total / PAGE_SIZE) : null;
  const statusLabel = statusFilter === 'all'
    ? 'Hepsi'
    : statusFilter
      ? (STATUS_LABELS[statusFilter] || statusFilter)
      : 'Aktif + kullanıldı + iptal';

  return (
    <div className="admin-page-content qr-page">
      <header className="qr-hero">
        <div className="qr-hero-copy">
          <p className="qr-kicker">Operasyon</p>
          <h1 className="qr-title">QR Kodları</h1>
          <p className="qr-sub">
            Tüm QR kayıtları burada: aktif, kullanıldı, iptal. Süresi dolanlar varsayılanda gizlenir.
          </p>
        </div>
      </header>

      <div className="qr-summary" aria-label="Özet ve filtreler">
        <div className="qr-summary-metric qr-summary-metric--discount">
          <span className="qr-summary-metric-label">Toplam indirim</span>
          <span className="qr-summary-metric-value">
            {discountTotal == null ? '—' : `${fmtAmount(discountTotal)} ₺`}
          </span>
        </div>
        <div className="qr-summary-divider" aria-hidden />
        <div className="qr-search-wrap">
          <svg className="qr-search-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="qr-search"
            placeholder="Seçeneğe göre ara…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
        </div>
        <div className="qr-status-seg" role="tablist" aria-label="Durum filtresi">
          {[
            { value: '', label: 'Aktif + kullanıldı + iptal' },
            { value: 'used', label: 'Kullanıldı' },
            { value: 'active', label: 'Aktif' },
            { value: 'cancelled', label: 'İptal' },
            { value: 'expired', label: 'Süresi doldu' },
            { value: 'pending', label: 'Beklemede' },
            { value: 'all', label: 'Hepsi' },
          ].map((opt) => (
            <button
              key={opt.value || 'default'}
              type="button"
              role="tab"
              aria-selected={statusFilter === opt.value}
              className={`qr-status-seg-btn${statusFilter === opt.value ? ' is-active' : ''}`}
              onClick={() => { setStatusFilter(opt.value); setPage(0); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="qr-summary-meta" aria-live="polite">
          <span className="qr-summary-total">
            {total == null ? '—' : total.toLocaleString('tr-TR')}
          </span>
          <span className="qr-summary-label">
            kayıt
            {search.trim() ? ` · “${search.trim()}”` : ''}
            {' · '}
            {statusLabel}
          </span>
        </p>
        <button type="button" className="qr-refresh" onClick={fetchData} disabled={loading}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0114.13-3.36L23 10M1 14l5.36 4.36A9 9 0 0020.49 15" />
          </svg>
          {loading ? 'Yükleniyor…' : 'Yenile'}
        </button>
      </div>

      <section className="qr-cleanup-bar" aria-labelledby="qr-cleanup-title">
        <div className="qr-cleanup-bar-copy">
          <h2 id="qr-cleanup-title" className="qr-cleanup-bar-title">Süresi dolanları temizle</h2>
          <p className="qr-cleanup-bar-sub">
            Yalnızca <StatusBadge status="expired" /> kayıtlar silinir.
          </p>
          <div className="qr-cleanup-stat" aria-live="polite">
            {expiredCountLoading ? (
              <span className="qr-cleanup-stat-loading">Sayılıyor…</span>
            ) : expiredTotal === null ? (
              <span className="qr-cleanup-stat-muted">Sayı alınamadı</span>
            ) : expiredTotal === 0 ? (
              <span className="qr-cleanup-stat-muted">Silinecek kayıt yok</span>
            ) : (
              <>
                <span className="qr-cleanup-stat-num">{expiredTotal.toLocaleString('tr-TR')}</span>
                <span className="qr-cleanup-stat-label">süresi dolmuş</span>
                {!cleanupConfirmOpen ? (
                  <button type="button" className="qr-cleanup-link" onClick={showExpiredInList}>
                    Listede göster
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
        <div className="qr-cleanup-bar-cta">
          {cleanupConfirmOpen ? (
            <div className="qr-cleanup-confirm" role="alertdialog" aria-labelledby="qr-cleanup-confirm-text">
              <p id="qr-cleanup-confirm-text" className="qr-cleanup-confirm-text">
                <strong>{countExpiredQrCodes().toLocaleString('tr-TR')}</strong> kayıt kalıcı silinecek.
              </p>
              <div className="qr-cleanup-confirm-btns">
                <button type="button" className="qr-cleanup-confirm-cancel" onClick={cancelCleanup}>
                  İptal
                </button>
                <button
                  type="button"
                  className="qr-cleanup-confirm-ok"
                  onClick={confirmCleanup}
                  disabled={cleanupLoading}
                >
                  {cleanupLoading ? 'Siliniyor…' : 'Evet, sil'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="qr-cleanup-btn"
              onClick={requestCleanup}
              disabled={loading || cleanupLoading || expiredCountLoading || expiredTotal === 0}
              title={
                expiredTotal === 0
                  ? 'Silinecek süresi dolmuş kayıt yok'
                  : 'Yalnızca Süresi doldu durumundaki kayıtları sil'
              }
            >
              {cleanupLoading ? 'Siliniyor…' : 'Temizle'}
            </button>
          )}
        </div>
      </section>

      {error ? (
        <p className="qr-alert qr-alert--error" role="alert">
          {error}
        </p>
      ) : null}

      {cleanupMessage ? (
        <p className="qr-alert qr-alert--success" role="status">
          {cleanupMessage}
        </p>
      ) : null}

      {userLookupError ? (
        <p className="qr-alert qr-alert--warning">
          Müşteri bilgisi yüklenemedi (
          <code>sql/12_admin_qr_user_info.sql</code> uygulandı mı?): {userLookupError}
        </p>
      ) : null}

      <div className="qr-table-card">
        <div className="qr-table-scroll">
          <table className="qr-table">
            <thead>
              <tr>
                {columns.map((col) => {
                  const isSorted = col.sortable !== false && sortCol === col.key;
                  const thClass = [
                    col.sortable !== false ? 'sortable' : '',
                    isSorted ? 'sorted' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <th
                      key={col.key}
                      className={thClass || undefined}
                      onClick={() => (col.sortable !== false ? handleSort(col.key) : undefined)}
                    >
                      {col.label}
                      {isSorted ? (
                        <span className="qr-sort-arrow">{sortAsc ? '↑' : '↓'}</span>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="qr-loading">
                    <span className="qr-loading-inner">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" opacity="0.25" />
                        <path d="M21 12a9 9 0 00-9-9" />
                      </svg>
                      Yükleniyor…
                    </span>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="qr-empty">
                    Kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const expanded = expandedRowId === row.id;
                  const canExpand = Boolean(row.check_id);
                  const assignedDiscountId = userById.get(row.creator_id)?.discount_id;
                  const effectiveAppliedId = resolveEffectiveAppliedDiscountId({
                    storedAppliedId: row.applied_discount_id,
                    assignedGlobalId: assignedDiscountId,
                    propertyId: row.property_id,
                    groupIndex,
                  });
                  return (
                  <Fragment key={row.id}>
                    <tr
                      className={[
                        canExpand ? 'qr-row--expandable' : '',
                        expanded ? 'qr-row--expanded' : '',
                      ].filter(Boolean).join(' ') || undefined}
                      onClick={canExpand ? () => toggleCheckRow(row) : undefined}
                      onKeyDown={canExpand ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleCheckRow(row);
                        }
                      } : undefined}
                      tabIndex={canExpand ? 0 : undefined}
                      role={canExpand ? 'button' : undefined}
                      aria-expanded={canExpand ? expanded : undefined}
                      title={canExpand ? 'Simpra check detayı' : undefined}
                    >
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={col.wrap ? 'wrap-cell' : undefined}
                        >
                          {col.render(row)}
                        </td>
                      ))}
                    </tr>
                    {expanded && row.check_id ? (
                      <tr className="qr-check-detail-row">
                        <td colSpan={columns.length}>
                          <SimpraCheckDetailPanel
                            checkId={row.check_id}
                            cacheEntry={checkCache.get(row.check_id)}
                            onLoad={loadSimpraCheck}
                            assignedDiscountId={assignedDiscountId}
                            appliedDiscountId={effectiveAppliedId}
                            storedAppliedId={row.applied_discount_id}
                            groupIndex={groupIndex}
                            propertyId={row.property_id}
                            qrRowId={row.id}
                            onAppliedSaved={handleAppliedSaved}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages !== null && totalPages > 1 ? (
          <nav className="qr-pagination" aria-label="Sayfalama">
            <span className="qr-page-info">
              Sayfa {page + 1} / {totalPages}
            </span>
            <div className="qr-page-buttons">
              <button
                type="button"
                className="qr-page-btn"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← Önceki
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                const offset = Math.max(0, Math.min(page - 3, totalPages - 7));
                const p = offset + i;
                return (
                  <button
                    key={p}
                    type="button"
                    className={`qr-page-btn${p === page ? ' qr-page-btn--active' : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p + 1}
                  </button>
                );
              })}
              <button
                type="button"
                className="qr-page-btn"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              >
                Sonraki →
              </button>
            </div>
          </nav>
        ) : null}
      </div>
    </div>
  );
}
